<template>
  <div class="match-list">
    <p v-if="matches.length === 0" class="match-empty">
      Für diese Saison sind keine Spiele erfasst.
    </p>

    <ol v-else class="match-rows">
      <li v-for="match in matches" :key="match.date" class="match-row">
        <span class="match-day">{{ matchdayLabel(match) }}</span>

        <img class="match-logo" :src="match.evnt.image" :alt="''" loading="lazy" />

        <span class="match-opponent">
          <span class="match-opponent-name">{{ opponentName(match.evnt) }}</span>
          <span class="match-venue" :title="match.evnt.home === false ? 'Auswärts' : 'Heim'">
            {{ match.evnt.home === false ? 'A' : 'H' }}
          </span>
        </span>

        <span class="match-result" :style="{ background: resultColor(match.evnt) }"
              :title="resultTitle(match.evnt)">
          {{ match.evnt.text || '–' }}
        </span>

        <span class="match-score">
          <span class="match-score-value">{{ formatScore(match.score) }}</span>
          <span class="match-score-track">
            <span class="match-score-fill"
                  :style="{ width: barWidth(match.score), background: moodColor(match.score) }"></span>
          </span>
        </span>

        <span class="match-date">{{ formatDateShort(match.date) }}</span>
      </li>
    </ol>
  </div>
</template>

<script>
import * as utils from '../../utils.js';
import {
  formatDateShort, formatScore, getMatchEntries, moodColor, opponentLabel, resultLabel
} from './moodHistory.js';

/**
 * Variante A2 des Stimmungsverlaufs: eine Zeile je Spiel.
 *
 * Ergaenzt die Kurve um genau das, was dort nicht mehr hineinpasst - Gegner, Ergebnis und
 * Stimmungswert im Klartext. Zugleich die zugaengliche Lesart der Daten: jeder Wert steht
 * als Text da und braucht weder Hover noch Farbunterscheidung.
 */
export default {
  name: 'MoodMatchList',
  props: {
    season: { type: Object, required: true }
  },
  computed: {
    matches() {
      return getMatchEntries(this.season);
    }
  },
  methods: {
    formatScore,
    formatDateShort,
    moodColor,
    resultColor(evnt) {
      return utils.getColorForEvent(evnt);
    },
    resultTitle(evnt) {
      return resultLabel(evnt);
    },
    opponentName(evnt) {
      // Ohne opp-Feld (aeltere API) bleibt nur der Spieltags-Freitext als Beschriftung.
      return evnt.opp || opponentLabel(evnt) || 'Gegner unbekannt';
    },
    matchdayLabel(match) {
      if (match.matchday !== null) return match.matchday + '.';
      return match.evnt.md && match.evnt.md.startsWith('Pokal') ? 'DFB' : '–';
    },
    barWidth(score) {
      const value = Math.max(0, Math.min(10, Number(score) || 0));
      return (value * 10) + '%';
    }
  }
};
</script>

<style scoped>
.match-list {
  background: var(--chart-surface);
  border-radius: var(--radius-md);
  padding: var(--spacing-sm);
}

.match-empty {
  margin: 0;
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--chart-ink-mute);
}

.match-rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2px;
}

/* Unter 576px zweizeilig: Spieltag, Wappen, Gegner und Ergebnis oben, Wert und Balken darunter.
   Einzeilig blieben bei 390px Geraetebreite nur rund 70px fuer den Gegnernamen uebrig - der
   wurde dann auf ein, zwei Buchstaben gekuerzt. */
.match-row {
  display: grid;
  grid-template-columns: 2rem 26px minmax(0, 1fr) auto;
  grid-template-areas:
    "day logo opponent result"
    "day logo score    score";
  align-items: center;
  column-gap: var(--spacing-xs);
  row-gap: 2px;
  padding: 7px var(--spacing-xs);
  border-radius: var(--radius-sm);
  transition: background 0.2s ease;
}

.match-day { grid-area: day; }
.match-logo { grid-area: logo; }
.match-opponent { grid-area: opponent; }
.match-result { grid-area: result; }
.match-score { grid-area: score; }

.match-row:hover {
  background: rgba(255, 255, 255, 0.05);
}

.match-day {
  color: var(--chart-ink-mute);
  font-size: var(--fs-sm);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.match-logo {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.match-opponent {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.match-opponent-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.match-venue {
  flex-shrink: 0;
  font-size: var(--fs-sm);
  font-weight: 700;
  color: var(--chart-ink-mute);
  border: 1px solid var(--chart-grid);
  border-radius: 3px;
  padding: 0 4px;
  line-height: 1.4;
}

.match-result {
  color: var(--chart-surface);
  font-weight: 700;
  font-size: var(--fs-sm);
  text-align: center;
  border-radius: var(--radius-sm);
  padding: 2px 7px;
  font-variant-numeric: tabular-nums;
  /* "2:4 n.E." darf nicht umbrechen - sonst waechst die ganze Zeile auf doppelte Hoehe. */
  white-space: nowrap;
}

.match-score {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.match-score-value {
  font-size: var(--fs-sm);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  min-width: 2.4rem;
}

.match-score-track {
  flex: 1 1 auto;
}

.match-score-track {
  height: 4px;
  border-radius: 999px;
  background: var(--chart-grid);
  overflow: hidden;
}

.match-score-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
}

/* Das Datum ist die erste Information, die bei Platzmangel weichen kann - der Spieltag
   steht ohnehin links. */
.match-date {
  display: none;
  color: var(--chart-ink-mute);
  font-size: var(--fs-sm);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

/* Ab 576px ist Platz fuer eine Zeile: Spieltag, Wappen, Gegner, Ergebnis, Wert, Datum. */
@media (min-width: 576px) {
  .match-row {
    grid-template-columns: 2.2rem 26px minmax(0, 1fr) 3.4rem 6rem 3.4rem;
    grid-template-areas: "day logo opponent result score date";
    row-gap: 0;
  }

  .match-date {
    display: block;
    grid-area: date;
  }
}

/* Ab Desktop zweispaltig - 38 Zeilen untereinander waeren sonst eine sehr lange Karte. */
@media (min-width: 992px) {
  .match-rows {
    grid-template-columns: 1fr 1fr;
    gap: 2px var(--spacing-lg);
  }
}
</style>
